'use client';

import React from 'react';

import { useScrollAnimation } from '@/hooks/useScrollAnimation';

import { BaseBalancedContentSectionData } from './balancedContentSection.types';

interface BalancedContentSectionProps {
  data: BaseBalancedContentSectionData;
  mediaContent: React.ReactNode;
  textContent: React.ReactNode;
  spacingClassName?: string;
  containerClassName?: string;
  textRatioClassName?: string;
  gapClassName?: string;
  textContentClassName?: string;
}

const BalancedContentSection: React.FC<BalancedContentSectionProps> = ({
  data,
  mediaContent,
  textContent,
  spacingClassName = 'py-8',
  containerClassName = 'content-wrapper',
  textRatioClassName,
  gapClassName = 'gap-12',
  textContentClassName = 'space-y-8',
}) => {
  // CENTRALIZED ANIMATION LOGIC
  const { ref, sectionClasses, childClasses, getChildStyle } =
    useScrollAnimation();

  const {
    textPosition = 'left',
    backgroundColor = 'bg-transparent', // Change default to transparent
    imageRatio = 'lg:w-1/2',
    reverseOnMobile = false,
    isDraft = false,
  } = data;

  const textRatio =
    textRatioClassName ??
    (imageRatio === 'lg:w-1/2' ? 'lg:w-1/2' : 'lg:w-[55%]');
  const mediaMobileOrderClass = reverseOnMobile
    ? 'order-2 lg:order-0'
    : 'order-1 lg:order-0';

  const textMobileOrderClass = reverseOnMobile
    ? 'order-1 lg:order-0'
    : 'order-2 lg:order-0';
  return (
    <section
      ref={ref}
      className={`${spacingClassName} relative overflow-hidden ${backgroundColor} ${sectionClasses}`}
    >
      {/* ... isDraft logic ... */}
      {isDraft && (
        <div
          data-testid="draft-badge"
          className="absolute top-4 right-4 z-50 rounded-full bg-yellow-500 px-3 py-1 text-sm font-medium text-white"
        >
          DRAFT
        </div>
      )}

      <div
        className={`${containerClassName} ${gapClassName} flex flex-col items-stretch lg:flex-row lg:items-center ${
          textPosition === 'right' ? '' : 'lg:flex-row-reverse'
        }`}
      >
        {/* MEDIA SLOT */}
        <div
          className={`w-full ${imageRatio} ${mediaMobileOrderClass} ${childClasses()}`}
          style={getChildStyle(200)}
        >
          {mediaContent}
        </div>

        {/* TEXT SLOT - The Critical Change */}
        <div
          className={`w-full ${textRatio} ${textMobileOrderClass} text-royal-500 flex min-h-full flex-col justify-center ${childClasses()}`}
          style={getChildStyle(400)}
        >
          <div className={`w-full ${textContentClassName}`}>{textContent}</div>
        </div>
      </div>
    </section>
  );
};

export default BalancedContentSection;
